import { useEffect, useRef, useState } from 'react';

// Module-level cache shared across mounts, scoped to the SPA session (cleared
// on full reload and on logout — see useAuth). Stale-while-revalidate: a page
// that was visited before renders its cached data INSTANTLY and refreshes in
// the background — fixes the 1–2 s blocking spinner on every navigation,
// where pages like Analytics and Consistency Check re-ran their server-side
// analysis from scratch each time the route mounted.
const cache = new Map<string, unknown>();
// Bumped on full invalidation (logout). An in-flight fetch from before the
// wipe must NOT repopulate the cache when it resolves — without the epoch, a
// slow response started under the previous identity could land after login.
let cacheEpoch = 0;

/** Drop a cached entry (or everything). Every mount revalidates anyway, so
 *  calling this after mutations is optional — its main job is wiping the
 *  cache on logout so the next login can't flash another user's data. */
export function invalidateCachedFetch(key?: string) {
  if (key === undefined) {
    cache.clear();
    cacheEpoch += 1;
  } else {
    cache.delete(key);
  }
}

/** Read a cached value without subscribing (for pages that manage their own
 *  fetch lifecycle, e.g. DashboardPage's post-mutation loadData refresh). */
export function peekCachedFetch<T>(key: string): T | undefined {
  return cache.get(key) as T | undefined;
}

/** Store a value for instant render on the next visit. */
export function primeCachedFetch<T>(key: string, value: T): void {
  cache.set(key, value);
}

interface CachedFetchState<T> {
  data: T | null;
  /** True only when there is no cached copy to show (first visit). */
  loading: boolean;
  /** True while a background refresh is in flight over cached data. */
  refreshing: boolean;
  error: boolean;
  /** True when a background refresh failed over cached data — the page is
   *  showing stale results (e.g. expired session: every refresh 401s but
   *  cached data would otherwise render indefinitely with no signal). */
  refreshFailed: boolean;
}

export function useCachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
): CachedFetchState<T> {
  const cached = cache.get(key) as T | undefined;
  const [data, setData] = useState<T | null>(cached ?? null);
  const [loading, setLoading] = useState(cached === undefined);
  const [refreshing, setRefreshing] = useState(cached !== undefined);
  const [error, setError] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Keep the latest fetcher without retriggering the effect — callers pass
  // inline closures.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    const epochAtStart = cacheEpoch;
    const hadCache = cache.has(key);
    // Re-seed state for the (possibly new) key. Without this, a key change
    // kept showing the PREVIOUS key's data until the fetch resolved — and a
    // failed fetch on the new key reported error over the old key's data.
    setData((cache.get(key) as T | undefined) ?? null);
    setError(false);
    setRefreshFailed(false);
    setRefreshing(hadCache);
    setLoading(!hadCache);
    fetcherRef.current()
      .then((fresh) => {
        // Don't repopulate a cache that was wiped (logout) mid-flight.
        if (cacheEpoch === epochAtStart) cache.set(key, fresh);
        if (cancelled) return;
        setData(fresh);
        setError(false);
        setRefreshFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep showing stale data if we have it; only flag error when the
        // page has nothing to render — but surface the failed refresh so
        // callers can warn that the visible data is stale.
        if (!hadCache) setError(true);
        else setRefreshFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { data, loading, refreshing, error, refreshFailed };
}
